import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Button, XStack, YStack } from '@/components/ui';
import { useOpenChat } from '@/features/chat/use-open-chat';
import { ImportanceThresholdSlider } from '@/features/home/components/ImportanceThresholdSlider';
import { useCodingFollowUp } from '@/features/home/use-coding-follow-up';
import { useMinCardImportance } from '@/features/home/use-min-card-importance';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import { confirmProactiveChatDeletion } from '../proactive-agent-chat-deletion';
import { ProactiveAgentList } from '../proactive-agent-list';
import { useProactiveAgentSelection } from '../proactive-agent-selection';
import {
  useIsChangingProactiveAgents,
  useProactiveAgentsQuery,
  useInternalProactiveAgentView,
  useProactiveAgentMutation,
} from '../proactive-agent.query';
import { SettingsSection } from '../settings-section';

import { ProactiveAgentCreateDialog } from './ProactiveAgentCreateDialog';
import { ProactiveAgentDeleteDialog } from './ProactiveAgentDeleteDialog';

export const ProactiveAgentSettings = observer(
  function ProactiveAgentSettings() {
    const agents = useProactiveAgentsQuery();
    const changeAgent = useProactiveAgentMutation();
    const isChanging = useIsChangingProactiveAgents();
    const { instances } = useProactiveAgentSelection(agents.data);
    const setInternalView = useInternalProactiveAgentView(instances);
    const { openChat, isOpening } = useOpenChat();
    const [isCreating, setIsCreating] = useState(false);
    const [deletingAgent, setDeletingAgent] = useState<string>();
    const { minImportance } = useMinCardImportance();
    const { onlyLittlebirdRuns, setOnlyLittlebirdRuns } = useCodingFollowUp();

    if (!userStore.isInternalUser) return null;

    return (
      <SettingsSection.Root>
        <SettingsSection.Header>
          <SettingsSection.Title>Proactive agent</SettingsSection.Title>
          <SettingsSection.Description>
            Compare and manage proactive agents.
          </SettingsSection.Description>
        </SettingsSection.Header>
        <SettingsSection.Group>
          <SettingsSection.Item searchId="proactive-agent.view">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Internal proactive agent view
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                When off, this device shows what users who are not internal see:
                no agent controls in the home settings, and no agent chats or
                routines in the lists. The agents keep running.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <SettingsSection.Switch
              checked={settingsStore.internalProactiveAgentView}
              onCheckedChange={setInternalView}
              aria-label="Internal proactive agent view"
            />
          </SettingsSection.Item>
          <SettingsSection.Item searchId="proactive-agent.importance">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Minimum card importance
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Hides rated cards below this. Cards without a rating always
                show.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <YStack className="w-72 shrink-0 gap-2">
              <ImportanceThresholdSlider />
              <XStack className="justify-end text-xs tabular-nums text-foreground-muted">
                <span>{minImportance}</span>
              </XStack>
            </YStack>
          </SettingsSection.Item>
          <SettingsSection.Item searchId="proactive-agent.coding-follow-up">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Coding follow-ups: only runs started from cards
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Off: the coding follow-up agent suggests next steps for every
                session of a connected coding agent. On: only for runs you start
                from a Littlebird card.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <SettingsSection.Switch
              checked={onlyLittlebirdRuns}
              onCheckedChange={setOnlyLittlebirdRuns}
              aria-label="Coding follow-ups: only runs started from cards"
            />
          </SettingsSection.Item>
        </SettingsSection.Group>
        <SettingsSection.Group
          title="My agents"
          trailing={
            <Button
              variant="secondary"
              size="sm"
              disabled={agents.isPending || agents.isError}
              onClick={() => setIsCreating(true)}
            >
              Create agent
            </Button>
          }
        >
          <SettingsSection.Item
            searchId="proactive-agent.run"
            direction="vertical"
            className="w-full gap-5"
          >
            <ProactiveAgentList
              showBuiltIn
              renderActions={(row) => {
                const { agent, chat, canStart, isBuiltIn } = row;
                // Goes in the last button row, not a row of its own
                const deleteAgentButton = !isBuiltIn ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    aria-label={`Delete ${agent}`}
                    tooltip="Delete this agent, along with its chat, routines and action cards."
                    tooltipOptions={{ className: 'max-w-xs' }}
                    disabled={isChanging}
                    onClick={() => setDeletingAgent(agent)}
                  >
                    Delete agent
                  </Button>
                ) : null;
                return chat ? (
                  <>
                    {settingsStore.internalProactiveAgentView ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        tooltip="Open the agent's chat without starting a run."
                        tooltipOptions={{ className: 'max-w-xs' }}
                        disabled={isOpening}
                        onClick={() => void openChat(chat.chat_id)}
                      >
                        Open chat
                      </Button>
                    ) : null}
                    {chat.state !== 'stopped' ? (
                      <>
                        <Button
                          size="sm"
                          tooltip={
                            chat.state === 'paused'
                              ? 'Resume the agent first. A paused agent does not run.'
                              : 'Send the agent its daily review message now, in its chat, without waiting for the schedule.'
                          }
                          tooltipOptions={{ className: 'max-w-xs' }}
                          disabled={isChanging || chat.state === 'paused'}
                          onClick={() =>
                            changeAgent.mutate({ kind: 'run', instance: chat })
                          }
                        >
                          Run now
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          tooltip={
                            chat.state === 'paused'
                              ? 'Let the agent get its daily review and new-integration messages again, and resume the routines paused with it.'
                              : 'Stop sending this agent its daily review and new-integration messages, and pause its routines. You can resume it later.'
                          }
                          tooltipOptions={{ className: 'max-w-xs' }}
                          disabled={isChanging}
                          onClick={() =>
                            changeAgent.mutate({
                              kind:
                                chat.state === 'paused' ? 'resume' : 'pause',
                              instance: chat,
                            })
                          }
                        >
                          {chat.state === 'paused' ? 'Resume' : 'Pause'}
                        </Button>
                      </>
                    ) : null}
                    <Button
                      size="sm"
                      variant="destructive"
                      tooltip="Permanently delete this agent's chat, its routines and their run history, and the action cards those runs produced."
                      tooltipOptions={{ className: 'max-w-xs' }}
                      disabled={isChanging}
                      onClick={async () => {
                        if (await confirmProactiveChatDeletion(chat))
                          changeAgent.mutate({
                            kind: 'delete-chat',
                            instance: chat,
                          });
                      }}
                    >
                      Delete chat
                    </Button>
                    {deleteAgentButton}
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      aria-label={`Run ${agent} now`}
                      tooltip="Create this agent's chat and send it the initial prompt."
                      tooltipOptions={{ className: 'max-w-xs' }}
                      disabled={isChanging || !canStart}
                      onClick={() =>
                        changeAgent.mutate({ kind: 'start', agents: [agent] })
                      }
                    >
                      Run now
                    </Button>
                    {deleteAgentButton}
                  </>
                );
              }}
            />
          </SettingsSection.Item>
          {agents.isError ? (
            <Button variant="secondary" onClick={() => void agents.refetch()}>
              Retry loading agents
            </Button>
          ) : null}
        </SettingsSection.Group>
        {isCreating && agents.data ? (
          <ProactiveAgentCreateDialog
            configurations={agents.data.configurations}
            startableAgents={agents.data.startable_agents}
            defaultAgent={agents.data.default_agent}
            onClose={() => setIsCreating(false)}
          />
        ) : null}
        {deletingAgent ? (
          <ProactiveAgentDeleteDialog
            agent={deletingAgent}
            isPending={isChanging}
            onClose={() => setDeletingAgent(undefined)}
            onConfirm={() =>
              changeAgent.mutate(
                { kind: 'delete-agent', agent: deletingAgent },
                { onSuccess: () => setDeletingAgent(undefined) }
              )
            }
          />
        ) : null}
      </SettingsSection.Root>
    );
  }
);
